import { BookSnapshot, Level } from "./types";

// Format large numbers with K/M suffixes
export const formatValue = (value: number): string => {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(3) + "M";
  } else if (value >= 1000) {
    return (value / 1000).toFixed(3) + "K";
  } else {
    return value.toFixed(3);
  }
};

// Parse very small prices into parts for subscript notation: 0.000000001 → { zeroCount: 8, significant: "1" }
export type SmallPriceParts = { zeroCount: number; significant: string };

export const parseSmallPrice = (price: number): SmallPriceParts | null => {
  const priceStr = price.toFixed(20);
  const match = priceStr.match(/^0\.(0+)([1-9]\d*?)0*$/);
  if (!match) return null;
  return { zeroCount: match[1]!.length, significant: match[2]! };
};

// Format very small prices with subscript notation as plain text: 0.000000001 → "0.0{8}1"
export const formatSmallPrice = (price: number): string => {
  const parts = parseSmallPrice(price);
  if (!parts) return price.toString();
  return `0.0{${parts.zeroCount}}${parts.significant}`;
};

// Format price based on precision
export const formatPrice = (price: number, precision: number): string => {
  if (price > 0 && price < 0.00001) {
    return formatSmallPrice(price);
  }
  const decimalPlaces = precision >= 1 ? 0 : Math.max(0, -Math.log10(precision));
  return price.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

// Process order book levels for display
export const processOrderBookLevels = (levels: [number, number][]) => {
  return levels.map(([price, amount]) => ({
    price,
    amount,
    quoteValue: price * amount,
  }));
};

// Calculate aggregated data for order book levels
export const calculateAggregatedData = (
  levels: Array<{ price: number; amount: number; quoteValue: number }>,
  upToIndex: number,
  side: "bids" | "asks",
  isReversed: boolean,
) => {
  let relevantLevels;

  if (side === "asks" && isReversed) {
    // For asks with reverse=true: best ask is at the END, so aggregate from end backwards
    relevantLevels = levels.slice(upToIndex);
  } else {
    // For bids (no reverse): best bid is at the START, so aggregate from start forwards
    relevantLevels = levels.slice(0, upToIndex + 1);
  }

  const totalTokens = relevantLevels.reduce((sum, level) => sum + level.amount, 0);
  const totalQuote = relevantLevels.reduce((sum, level) => sum + level.quoteValue, 0);

  // Simple average price calculation - just average between the first and last relevant levels
  let avgPrice = 0;
  if (relevantLevels.length === 0) {
    avgPrice = 0;
  } else if (relevantLevels.length === 1) {
    avgPrice = relevantLevels[0]?.price || 0;
  } else {
    const firstPrice = relevantLevels[0]?.price || 0;
    const lastPrice = relevantLevels[relevantLevels.length - 1]?.price || 0;
    avgPrice = (firstPrice + lastPrice) / 2;
  }

  return {
    avgPrice,
    totalTokens,
    totalQuote,
    levelCount: relevantLevels.length,
  };
};

// Calculate bar width percentage for liquidity visualization
export const calculateBarWidth = (quoteValue: number, maxQuoteValue: number): number => {
  return (quoteValue / Math.max(maxQuoteValue, 1)) * 100;
};

// Determine if a row should be highlighted
export const shouldHighlightRow = (index: number, activeIndex: number | null, side: "bids" | "asks"): boolean => {
  if (activeIndex === null) return false;

  return side === "asks"
    ? index >= activeIndex // For asks: highlight from clicked/hovered row up to top (index 0)
    : index <= activeIndex; // For bids: highlight from clicked/hovered row down to bottom
};

// Get popover positioning based on layout
export const getPopoverSide = (layout: "default" | "tablet" | "mobile"): "top" | "right" | "bottom" | "left" => {
  if (layout === "mobile") {
    return "top"; // Mobile positioning
  }
  return "left"; // Desktop/tablet positioning
};

export const aggregateOrderBook = (snapshot: BookSnapshot, precision: number) => {
  const aggregateLevel = (levels: Level[], isPrecisionBased = true): Level[] => {
    const aggregated = new Map<string, number>();

    for (const level of levels) {
      const price = parseFloat(level.price);
      const amount = parseFloat(level.amount);

      // Round price to precision
      const roundedPrice = isPrecisionBased ? Math.floor(price / precision) * precision : price;
      const priceKey = roundedPrice.toFixed(Math.max(0, -Math.log10(precision)));

      aggregated.set(priceKey, (aggregated.get(priceKey) || 0) + amount);
    }

    return Array.from(aggregated.entries())
      .map(([price, amount]) => ({
        price,
        amount: amount.toFixed(8),
      }))
      .sort((a, b) => parseFloat(b.price) - parseFloat(a.price)); // Sort descending
  };

  return {
    bids: aggregateLevel(snapshot.bids),
    asks: aggregateLevel(snapshot.asks).reverse(), // Asks should be ascending
  };
};
